/** "Raise a question about this record" - the way into the assurance queue from the thing asked about
 * (gate 27).
 *
 * Raising a request needed a record id pasted out of an address bar: an auditor looking at a figure's
 * receipt, with the record one click away, had to copy a UUID into a form on another screen. This
 * is the link that does it for them. It carries the id to `/assurance?record=`, which seeds the
 * raise form and nothing more - the server still resolves the id and clamps it to the engagement, so
 * it is a convenience and not a second way in.
 *
 * **Shown to the roles that can raise, and to nobody else** (design rule 6: a control that
 * predictably refuses is the defect). That is an Auditor or an Admin, mirrored from the server's
 * `MAY_RAISE`. While `/auth/me` is in flight, and when it fails, nothing is shown: an absent link is
 * recoverable, and a link shown to a Viewer is a promise the form then breaks.
 */

import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { MAY_RAISE } from "../api/assurance";
import { useMe } from "../api/auth";
import { keepSearch } from "../screens/search";
import styles from "./AssuranceAbout.module.css";

export function RaiseAboutThis({
  recordId,
  frame,
}: {
  recordId: string;
  /** Wraps the link where the surface is a grid of panels, so a bare paragraph does not land in one
   * cell of it. Called only when the link is shown. */
  frame?: (children: ReactNode) => ReactNode;
}) {
  const { data: me } = useMe();
  if (!me || !MAY_RAISE.includes(me.role)) return null;
  const link = (
    <p className={styles.raise}>
      <Link
        to="/assurance"
        search={(prev) => ({ ...keepSearch(prev), record: recordId })}
        className={styles.link}
        data-testid="assurance-raise-from-record"
      >
        Raise a question about this record
      </Link>
    </p>
  );
  return frame ? frame(link) : link;
}
